import { differenceInDays } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { DEFAULT_CLINIC_TIME_ZONE } from '#src/addressTimezone.ts'

const CUTOFF_TIME = '15:00:00'
// getUTCDay numbering.
const SUNDAY = 0
const SATURDAY = 6

interface CycleTableRow {
  cutoff: string // ISO date-only, 15:00 clinic-local that day
  start: string // ISO date-only, midnight clinic-local
}

// Transcribed from the GraneRx printout ("Seen Health Pace Cycle Schedule
// 2026") summarized in thoughts/shared/tasks/med-refill-tasking/00-master-summary.md.
// Not in the repo or any FHIR field — V1 hardcodes it. Expires at the last
// CUTOFF, not the last start: nextCutoff returns undefined for any `now` after
// 2026-12-28T15:00 clinic-local, ten days before that row's 2027-01-07 start.
// Cadence is 28 days cutoff-to-cutoff, start = cutoff + 10 days, EXCEPT the
// 2026-09-04 row: GraneRx pulled that cutoff to the Friday before Labor Day, so
// it is 13 days to its start and 25/31 days to its neighboring cutoffs.
const CYCLE_TABLE: CycleTableRow[] = [
  { cutoff: '2025-12-29', start: '2026-01-08' },
  { cutoff: '2026-01-26', start: '2026-02-05' },
  { cutoff: '2026-02-23', start: '2026-03-05' },
  { cutoff: '2026-03-23', start: '2026-04-02' },
  { cutoff: '2026-04-20', start: '2026-04-30' },
  { cutoff: '2026-05-18', start: '2026-05-28' },
  { cutoff: '2026-06-15', start: '2026-06-25' },
  { cutoff: '2026-07-13', start: '2026-07-23' },
  { cutoff: '2026-08-10', start: '2026-08-20' },
  { cutoff: '2026-09-04', start: '2026-09-17' }, // Labor Day holiday row — cutoff itself is Friday
  { cutoff: '2026-10-05', start: '2026-10-15' },
  { cutoff: '2026-11-02', start: '2026-11-12' },
  { cutoff: '2026-11-30', start: '2026-12-10' },
  { cutoff: '2026-12-28', start: '2027-01-07' },
]

export interface CycleRow {
  cutoff: Date // 15:00 clinic-local that calendar day
  start: Date // Thursday start (or 2026-09-17 holiday row)
}

const ROWS: CycleRow[] = CYCLE_TABLE.map((row) => ({
  cutoff: fromZonedTime(
    `${row.cutoff}T${CUTOFF_TIME}`,
    DEFAULT_CLINIC_TIME_ZONE
  ),
  start: fromZonedTime(`${row.start}T00:00:00`, DEFAULT_CLINIC_TIME_ZONE),
}))

// Next cutoff on or after `now`. `now` is inclusive of the exact cutoff instant
// so a run kicked off exactly at 15:00 clinic-local still sees it.
// Returns undefined once the table is exhausted (last cutoff 2026-12-28).
export function nextCutoff(now: Date): CycleRow | undefined {
  return ROWS.find((row) => row.cutoff.getTime() >= now.getTime())
}

// The cutoff before `cutoff`, or undefined for the first row. Tasks are filed
// against `nextCutoff`, so a task for a given cutoff cannot have been authored
// earlier than this — which makes it a safe floor for reading them back.
export function previousCutoff(cutoff: Date): CycleRow | undefined {
  const index = ROWS.findIndex(
    (row) => row.cutoff.getTime() >= cutoff.getTime()
  )
  return index > 0 ? ROWS[index - 1] : undefined
}

// The business day before the cutoff. The gap is the point: a provider who
// misses the due date still has a working day left to order before GraneRx
// stops accepting, so the buffer applies to every row including a cutoff that
// already falls on a Friday. Holidays are not modelled — only weekends are
// skipped — so a cutoff on any weekday resolves without special-casing the
// calendar's shape.
//
// The step happens on clinic civil dates and the result is rebuilt at the
// cutoff's own wall-clock time: stepping back as an absolute span would drift
// an hour whenever the cutoff and its deadline sit on opposite sides of a DST
// change (2026-11-02 is PST; its deadline is PDT).
export function dueDateBeforeCutoff(cutoff: Date): Date {
  const [year, month, date] = formatInTimeZone(
    cutoff,
    DEFAULT_CLINIC_TIME_ZONE,
    'yyyy-MM-dd'
  )
    .split('-')
    .map(Number)

  const civil = new Date(Date.UTC(year, month - 1, date))
  do {
    civil.setUTCDate(civil.getUTCDate() - 1)
  } while (civil.getUTCDay() === SATURDAY || civil.getUTCDay() === SUNDAY)

  const wallTime = formatInTimeZone(
    cutoff,
    DEFAULT_CLINIC_TIME_ZONE,
    'HH:mm:ss'
  )
  return fromZonedTime(
    `${formatInTimeZone(civil, 'UTC', 'yyyy-MM-dd')}T${wallTime}`,
    DEFAULT_CLINIC_TIME_ZONE
  )
}

export function daysUntilCutoff(now: Date, cutoff: Date): number {
  return differenceInDays(cutoff, now)
}
